/**
 * Permission utility functions for checking user permissions
 */

import { useSelector } from 'react-redux';

/**
 * Get user information from localStorage
 * @returns {Object|null} User info object or null if not found
 */
const getUserInfo = () => {
  try {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    console.error('Error parsing user info from localStorage:', error);
    return null;
  }
};

/**
 * Get user roles from localStorage
 * @returns {Array<string>} Array of user roles
 */
const getUserRoles = () => {
  const userInfo = getUserInfo();
  return userInfo?.roles || [];
};

/**
 * Get user policies from localStorage
 * @returns {Array<string>} Array of user policies
 */
const getUserPolicies = () => {
  const userInfo = getUserInfo();
  return userInfo?.policies || [];
};

/**
 * Custom hook to get current user permissions from Redux state
 * This ensures components re-render when permissions change
 * @returns {Array<string>} Array of user policies
 */
export const usePermissions = () => {
  const { permissions = [] } = useSelector((state) => state.userPermissions || {});
  const localPolicies = getUserPolicies();
  
  // Return Redux permissions if available, otherwise fall back to localStorage
  return permissions.length > 0 ? permissions : localPolicies;
};

/**
 * Custom hook to check if user has a specific permission (reactive to Redux changes)
 * @param {string} permission - The permission to check
 * @returns {boolean} True if user has the permission
 */
export const useHasPermission = (permission) => {
  const permissions = usePermissions();
  const roles = getUserRoles();
  
  if (!permission) return false;
  
  // Check if user has SuperUser role (has all permissions)
  if (roles.includes('SuperUser')) {
    return true;
  }
  
  // Check if user has the specific permission
  return permissions.includes(permission);
};

/**
 * Custom hook to check if user has any of the specified permissions (reactive to Redux changes)
 * @param {Array<string>} permissionsToCheck - Array of permissions to check
 * @returns {boolean} True if user has any of the permissions
 */
export const useHasAnyPermission = (permissionsToCheck) => {
  const permissions = usePermissions();
  const roles = getUserRoles();
  
  if (!Array.isArray(permissionsToCheck) || permissionsToCheck.length === 0) return false;
  
  // Check if user has SuperUser role (has all permissions)
  if (roles.includes('SuperUser')) {
    return true;
  }
  
  // Check if user has any of the specified permissions
  return permissionsToCheck.some(perm => permissions.includes(perm));
};

/**
 * Custom hook to check if user has all of the specified permissions (reactive to Redux changes)
 * @param {Array<string>} permissionsToCheck - Array of permissions to check
 * @returns {boolean} True if user has all of the permissions
 */
export const useHasAllPermissions = (permissionsToCheck) => {
  const permissions = usePermissions();
  const roles = getUserRoles();
  
  if (!Array.isArray(permissionsToCheck) || permissionsToCheck.length === 0) return false;
  
  // Check if user has SuperUser role (has all permissions)
  if (roles.includes('SuperUser')) {
    return true;
  }
  
  // Check if user has all of the specified permissions
  return permissionsToCheck.every(perm => permissions.includes(perm));
};

/**
 * Check if user has a specific permission (non-reactive, for non-component code)
 * @param {string} permission - The permission to check
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (permission) => {
  if (!permission) return false;
  
  const userInfo = getUserInfo();
  if (!userInfo) return false;
  
  // Check if user has the specific permission in policies
  const policies = getUserPolicies();
  if (policies.includes(permission)) {
    return true;
  }
  
  // Check if user has SuperUser role (has all permissions)
  const roles = getUserRoles();
  if (roles.includes('SuperUser')) {
    return true;
  }
  
  return false;
};

/**
 * Check if user has any of the specified permissions
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean} True if user has any of the permissions
 */
export const hasAnyPermission = (permissions) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return false;
  
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean} True if user has all of the permissions
 */
export const hasAllPermissions = (permissions) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return false;
  
  return permissions.every(permission => hasPermission(permission));
};

/**
 * Check if user has a specific role
 * @param {string} role - The role to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (role) => {
  if (!role) return false;
  
  const roles = getUserRoles();
  return roles.includes(role);
};

/**
 * Check if user has any of the specified roles
 * @param {Array<string>} roles - Array of roles to check
 * @returns {boolean} True if user has any of the roles
 */
export const hasAnyRole = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  
  const userRoles = getUserRoles();
  return roles.some(role => userRoles.includes(role));
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = () => {
  const userInfo = getUserInfo();
  return !!(userInfo && userInfo.token);
};

/**
 * Check if user is SuperUser
 * @returns {boolean} True if user is SuperUser
 */
export const isSuperUser = () => {
  return hasRole('SuperUser');
};

